import type { TokenCredential } from "@azure/identity";
import { authedJsonResult } from "./http.js";
import type { ProbeResult } from "./data-plane-ready.js";

const FOUNDRY_SCOPE = "https://ai.azure.com/.default";

/** A cheap data-plane GET (list agents). 200 => ready; 404/5xx => not-ready (classified upstream). */
export function makeAgentsProbe(credential: TokenCredential, endpoint: string): () => Promise<ProbeResult> {
  return async () => {
    try {
      const r = await authedJsonResult<{ error?: { message?: string } }>({
        credential,
        scope: FOUNDRY_SCOPE,
        method: "GET",
        url: `${endpoint}/agents?api-version=v1`,
        okStatuses: [404, 500, 502, 503], // don't throw — return the status so the loop classifies it
      });
      if (r.status === 200) return { ok: true, status: 200, message: "" };
      const body = r.data?.error?.message ?? "";
      return { ok: false, status: r.status, message: `${r.status.toString()} ${body}`.trim() };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : String(e) };
    }
  };
}

/** A cheap per-agent data-plane GET (lookup by name). 200 => queryable; 404/5xx => not-yet (classified upstream). */
export function makeAgentProbe(credential: TokenCredential, endpoint: string, agentName: string): () => Promise<ProbeResult> {
  return async () => {
    try {
      const r = await authedJsonResult<{ error?: { message?: string } }>({
        credential,
        scope: FOUNDRY_SCOPE,
        method: "GET",
        url: `${endpoint}/agents/${agentName}?api-version=v1`,
        okStatuses: [404, 500, 502, 503], // don't throw — return the status so the loop classifies it
      });
      if (r.status === 200) return { ok: true, status: 200, message: "" };
      const body = r.data?.error?.message ?? "";
      return { ok: false, status: r.status, message: `${r.status.toString()} ${body}`.trim() };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : String(e) };
    }
  };
}
